import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);
  /** @type {number} */
  #animationRequestId = 0;
  /** @type {number} */
  #loadingId = 0;
  /** @type {number | undefined} */
  #loadingSafety;

  /** Padel USA: clear the loading ring the instant the kit-upsell popup is about to show. */
  #onUpsellShown = () => this.stopLoading();

  /** No-upsell path: once the add settles, give the popup a beat to open; if none, clear the ring. */
  #onCartSettled = () => {
    if (this.dataset.loading !== 'true') return;
    const id = this.#loadingId;
    setTimeout(() => {
      if (this.#loadingId === id) this.stopLoading();
    }, 350);
  };

  #onCartError = () => this.stopLoading();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    // Padel USA: drive the loading ring — cleared when the kit-upsell popup opens, when the add
    // settles with no upsell, on error, or by the safety net in startLoading().
    document.addEventListener('kit-upsell:shown', this.#onUpsellShown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartSettled);
    document.addEventListener(ThemeEvents.cartError, this.#onCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearResetTimeouts();
    this.refs.addToCartButton?.removeAttribute('data-added');
    this.removeEventListener('pointerenter', this.#preloadImage);
    document.removeEventListener('kit-upsell:shown', this.#onUpsellShown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartSettled);
    document.removeEventListener(ThemeEvents.cartError, this.#onCartError);
    clearTimeout(this.#loadingSafety);
  }

  #clearResetTimeouts() {
    if (!this.#resetTimeouts) return;
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Padel USA: show the loading ring inside the button (Gymshark-style) until the kit-upsell
   * popup opens or the add settles. Token-guarded so a stale timer can't clear a newer click.
   */
  startLoading() {
    this.#loadingId++;
    const id = this.#loadingId;
    this.dataset.loading = 'true';
    clearTimeout(this.#loadingSafety);
    // Safety net: never let the ring stick if no popup/cart event ever arrives.
    this.#loadingSafety = setTimeout(() => {
      if (this.#loadingId === id) this.stopLoading();
    }, 4000);
  }

  stopLoading() {
    if (this.dataset.loading !== 'true') return;
    delete this.dataset.loading;
    clearTimeout(this.#loadingSafety);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.startLoading();
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;
    const animationRequestId = ++this.#animationRequestId;

    // Clear any pending reset from prior animation runs.
    this.#clearResetTimeouts();

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    if (animationRequestId !== this.#animationRequestId || !this.isConnected) return;
    await onAnimationEnd(addToCartButton);
    if (animationRequestId !== this.#animationRequestId || !this.isConnected) return;

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      if (animationRequestId !== this.#animationRequestId) return;
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Delegated handler for notify back-in-stock button (works after morph replaces the form)
    this.addEventListener('click', this.#onNotifyBackInStockClick, { signal });
    // Enter in the notify email field sends the notify request — it must not submit the
    // surrounding product form (an add-to-cart of the sold-out variant).
    this.addEventListener('keydown', this.#onNotifyEmailKeydown, { signal });

    // Hide trust icons on load when product is out of stock (notify form visible)
    const notifyBlock = this.querySelector('.product_buy_grid.notify_button.padel-rackets.out_of_stok');
    const isOutOfStock = notifyBlock != null && !notifyBlock.hasAttribute('hidden');
    this.#syncTrustIconsVisibility(isOutOfStock);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();

    const quantityOffer = this.querySelector('pdp-quantity-upsell');
    if (quantityOffer?.dataset.guided === 'true' && !quantityOffer.hidden) {
      if (typeof quantityOffer.validateSelection !== 'function' || !quantityOffer.validateSelection()) return;
      if (this.#variantChangeInProgress) { quantityOffer.showPending(); return; }
    }
    if (quantityOffer?.isMixedChoice?.()) {
      if (this.dataset.mixedAddPending === 'true') return;
      this.dataset.mixedAddPending = 'true';
      try {
        const cartBefore = await this.#fetchAndUpdateCartQuantity();
        if (!cartBefore) {
          quantityOffer.showError('Could not check current availability. Please try again.');
          return;
        }
        quantityOffer.syncCartQuantities(cartBefore);
        if (this.#variantChangeInProgress) { quantityOffer.showPending(); return; }
        if (!quantityOffer.validateSelection()) return;
        const items = quantityOffer.getCartItems();
        if (!items) return;
        await this.#processBatchAddToCart(items, cartBefore);
      } finally {
        delete this.dataset.mixedAddPending;
      }
      return;
    }

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   * @param {Cart | null} [cartBefore]
   */
  #processBatchAddToCart(items, cartBefore = null) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    return fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const cartAfterError = cartBefore ? await this.#fetchAndUpdateCartQuantity() : null;
          const partialAdd = cartAfterError && items.some((item) => {
            const quantityFor = (currentCart) => currentCart.items
              .filter((line) => String(line.variant_id) === String(item.variantId))
              .reduce((sum, line) => sum + line.quantity, 0);
            return quantityFor(cartAfterError) > quantityFor(cartBefore);
          });
          const errorMessage = partialAdd
            ? 'Only available sleeves were added. Review the sizes and quantities in your cart.'
            : response.description || response.message;
          if (partialAdd) this.querySelector('pdp-quantity-upsell')?.showError(errorMessage);
          this.dispatchEvent(
            new CartErrorEvent(this.id, errorMessage, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              addToCartTextError.appendChild(document.createTextNode(errorMessage));
            }
            this.#setLiveRegionText(errorMessage);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent(cartAfterError ?? {}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        const cart = await this.#fetchAndUpdateCartQuantity();
        if (cartBefore && !cart) {
          this.querySelector('pdp-quantity-upsell')?.showError('Could not verify the added sleeves. Review your cart before checkout.');
          return;
        }
        if (cartBefore && cart) {
          const addedEverySize = items.every((item) => {
            const quantityFor = (currentCart) => currentCart.items
              .filter((line) => String(line.variant_id) === String(item.variantId))
              .reduce((sum, line) => sum + line.quantity, 0);
            return quantityFor(cart) - quantityFor(cartBefore) === item.quantity;
          });
          if (!addedEverySize) {
            this.querySelector('pdp-quantity-upsell')?.showError('Availability changed while adding. Review the sizes and quantities in your cart.');
            this.dispatchEvent(new CartAddEvent(cart, this.id, {
              didError: true,
              source: 'product-form-component',
              productId: this.dataset.productId,
            }));
            return;
          }
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
            quantityUpsellSizes: this.querySelector('pdp-quantity-upsell')?.getSizeSummary(),
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Hide trust icons (Free Shipping, Free Returns, Secure Payments) when product is out of stock
    const isOutOfStock = event.detail.resource == null || event.detail.resource.available === false;
    this.#syncTrustIconsVisibility(isOutOfStock);
    this.#syncSoldOutUI(isOutOfStock, event.detail.data.html, event.detail.resource?.id);

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };

  /**
   * Hide or show the product-details trust icons block (Free Shipping, Free Returns, Secure Payments).
   * Targets only the innermost .group-block that contains both strings, so we don't hide the whole column.
   * @param {boolean} isOutOfStock - When true, hide the block; when false, show it.
   */
  #syncTrustIconsVisibility = (isOutOfStock) => {
    const productDetails = this.closest('.product-details') || this.closest('[id^="ProductInformation"]');
    if (!productDetails) return;
    const allGroups = Array.from(productDetails.querySelectorAll('.group-block'));
    const withTrustText = allGroups.filter(
      (el) =>
        (el.textContent?.includes('Free Shipping') || el.textContent?.includes('Free shipping')) &&
        (el.textContent?.includes('Easy Returns') || el.textContent?.includes('Easy returns'))
    );
    // Pick the innermost group (the one that doesn't contain another matching group) so we don't hide the whole product details
    const trustIconsGroup = /** @type {HTMLElement | undefined} */ (
      withTrustText.find((g) => !withTrustText.some((other) => other !== g && g.contains(other)))
    );
    if (trustIconsGroup) {
      if (isOutOfStock) {
        trustIconsGroup.setAttribute('hidden', '');
      } else {
        trustIconsGroup.removeAttribute('hidden');
      }
    }
  };

  /**
   * Keep the sold-out UI in sync on variant change: the notify (back-in-stock)
   * grid shows only when the selected variant can't be bought, and the ATC
   * benefits trio (bbc cards) hides with it — the section morph does not
   * reliably update these `hidden` attributes in both directions.
   * The notify module itself is not morphed (a typed email survives), so its status line
   * ("Size 11 is sold out") and data-variant-id are copied from the freshly rendered section, and
   * a previous "You're on the list" confirmation resets for the newly selected size.
   * @param {boolean} isOutOfStock - True when the variant is unavailable/nonexistent.
   * @param {Document | Element} [html] - The re-rendered section from the variant update.
   * @param {number | string} [variantId] - The newly selected variant.
   */
  #syncSoldOutUI = (isOutOfStock, html, variantId) => {
    const notifyBlock = this.querySelector('.product_buy_grid.notify_button.out_of_stok');
    notifyBlock?.toggleAttribute('hidden', !isOutOfStock);
    if (notifyBlock) {
      const status = notifyBlock.querySelector('.pusa-bis__status');
      const freshStatus = html?.querySelector?.('product-form-component .pusa-bis__status');
      if (status && freshStatus) status.innerHTML = freshStatus.innerHTML;
      const notifyBtn = /** @type {HTMLElement | null} */ (notifyBlock.querySelector('.notify_btn'));
      if (notifyBtn && variantId) notifyBtn.dataset.variantId = String(variantId);
      const msgEl = /** @type {HTMLElement | null} */ (notifyBlock.querySelector('.notify_button_not'));
      const fields = /** @type {HTMLElement | null} */ (notifyBlock.querySelector('.notify_button_main'));
      if (msgEl?.classList.contains('susses')) {
        msgEl.classList.remove('susses');
        msgEl.style.display = 'none';
        msgEl.textContent = '';
        if (fields) fields.style.display = '';
      }
    }
    const scope = this.closest('.shopify-section, dialog, product-card') || this;
    scope.querySelectorAll('.bbc-wrap, .bbc-inline').forEach((el) => el.toggleAttribute('hidden', isOutOfStock));
  };

  /**
   * Show a notify-form message (error by default; `success` swaps the fields for a confirmation).
   * @param {HTMLElement} container - The `.notify_btn` element.
   * @param {string} text
   * @param {boolean} [success]
   */
  #showNotifyMessage = (container, text, success = false) => {
    const msgEl = /** @type {HTMLElement | null} */ (container.querySelector('.notify_button_not'));
    const emailEl = container.querySelector('input[type="email"]');
    if (msgEl) {
      msgEl.style.display = 'block';
      msgEl.textContent = text;
      msgEl.classList.toggle('susses', success);
    }
    if (emailEl) {
      if (success) emailEl.removeAttribute('aria-invalid');
      else emailEl.setAttribute('aria-invalid', 'true');
    }
  };

  /**
   * Enter in the notify email field = press "Notify me" (never submit the product form).
   * @param {KeyboardEvent} event
   */
  #onNotifyEmailKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLInputElement) || !target.matches('.notify_btn input[type="email"]')) return;
    event.preventDefault();
    const btn = /** @type {HTMLButtonElement | null} */ (target.closest('.notify_btn')?.querySelector('#notify_button, button') ?? null);
    btn?.click();
  };

  /**
   * Opt-in ticked → subscribe the notify email to the newsletter (owner decision 2026-10-09: real
   * checkbox, ticked by default). Klaviyo client subscriptions API on the public key → the
   * "Newsletter" list (SPqWQL), the same list Shopify footer signups sync into. Fire-and-forget: a blocked
   * a.klaviyo.com (ad-blockers) must never break the restock signup itself.
   * @param {HTMLElement} container - The `.notify_btn` element.
   * @param {string} email
   */
  #subscribeNotifyEmailToNewsletter = (container, email) => {
    const listId = (container.dataset.klaviyoListId || '').trim();
    const publicApiKey = (container.dataset.klaviyoPublicApiKey || '').trim();
    if (!listId || !publicApiKey) return;
    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicApiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock form',
            // No `subscriptions` field: this endpoint rejects it with a 400 ("not a valid field for
            // the resource 'profile'", seen live 2026-10-09). The client subscription itself records
            // email marketing consent, as the quiz/popup capture does.
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
    }).catch(() => {});
  };

  /**
   * Delegated click handler for the notify back-in-stock form.
   * Works even after variant update morphs the form (inline scripts in morphed HTML do not run).
   * @param {MouseEvent} event
   */
  #onNotifyBackInStockClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target.closest?.('#notify_button') || target.closest?.('.notify_btn button');
    if (!btn) return;

    const container = /** @type {HTMLElement} */ (btn.closest('.notify_btn'));
    if (!container) return;

    event.preventDefault();
    event.stopPropagation();

    const emailEl = /** @type {HTMLInputElement | null} */ (container.querySelector('input[type="email"]'));
    const email = (emailEl && emailEl.value) ? emailEl.value.trim() : '';
    // The product form sits inside this component (this.closest('form') was always null, so the
    // stale server-rendered data-variant-id was used after a size change).
    const form = btn.closest('form') ?? this.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    const variantId =
      (variantInput?.value?.trim() ?? '') || container.getAttribute('data-variant-id') || '';

    if (!email || (emailEl && !emailEl.checkValidity())) {
      this.#showNotifyMessage(container, email ? (container.dataset.labelErrorInvalid || 'Enter a valid email address.') : (container.dataset.labelErrorEmpty || 'Enter your email address.'));
      emailEl?.focus();
      return;
    }
    if (btn.getAttribute('aria-busy') === 'true') return;

    const vid = variantId ? parseInt(variantId, 10) : 0;
    const publicApiKey = (container.dataset.klaviyoPublicApiKey || '').trim();
    if (!vid || !publicApiKey) {
      this.#showNotifyMessage(container, container.dataset.labelErrorGeneric || 'Something went wrong — please try again.');
      return;
    }

    // "Also send me padel news and new drops" (ticked by default; unticked = restock alert only).
    const optin = container.closest('.pusa-bis')?.querySelector('input[name="notify_klaviyo_optin"]');
    const acceptsMarketing = optin instanceof HTMLInputElement && optin.checked;

    btn.setAttribute('aria-busy', 'true');
    // Klaviyo native back-in-stock (owner decision 2026-10-09: own the feature, no AMP app). Klaviyo
    // stores the subscription per Shopify variant, watches the synced catalog's inventory and releases
    // waiting shoppers into the "Back in Stock" flow. A repeat signup for the same size is accepted
    // again (202), so "already registered" needs no special case. The flow's email must be Live or
    // Manual, otherwise Klaviyo drops new subscribers instead of holding them.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(publicApiKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      signal: controller.signal,
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${vid}` } },
          },
        },
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          this.#showNotifyMessage(container, container.dataset.labelSuccess || "You're on the list. We'll email you as soon as it's back.", true);
          const notifyParent = /** @type {HTMLElement | null} */ (container.querySelector('.notify_button_main'));
          if (notifyParent) notifyParent.style.display = 'none';
          if (acceptsMarketing) this.#subscribeNotifyEmailToNewsletter(container, email);
          return;
        }
        const detail = await res.text().catch(() => '');
        if (res.status === 400 && /email/i.test(detail)) {
          this.#showNotifyMessage(container, container.dataset.labelErrorInvalid || 'Enter a valid email address.');
          return;
        }
        throw new Error(`klaviyo back-in-stock ${res.status}`);
      })
      .catch(() => {
        this.#showNotifyMessage(container, container.dataset.labelErrorGeneric || 'Something went wrong — please try again.');
      })
      .finally(() => {
        clearTimeout(timer);
        btn.removeAttribute('aria-busy');
      });
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
